import { useEffect, useMemo, useState } from "react";

import { Spinner } from "@blueprintjs/core";
import { useMediaQuery } from "react-responsive";

import { isAssist } from "@/components/helpers/workspace/permissions";
import { Box, Flex } from "@/components/layout/flexbox";
import { isMobileQuery } from "@/components/layout/Media";
import { BigButton } from "@/components/pages_flexible/explore/detail/styles";
import { SpecialtyRenderDataType } from "@/components/pieces/coaching/coach-profile/coachingUtils";
import { AlertMessage } from "@/components/pieces/coaching/coach-profile/ScheduleCall";
import ErrorModalContainer from "@/components/pieces/coaching/ErrorContainer";
import AssistantClientsSelector from "@/components/pieces/form/AssistantClientsSelector";
import SpecialtiesSelector from "@/components/pieces/form/SpecialtiesSelector";
import ProductSelector from "@/components/pieces/form/TimeSlotDurationSelector";
import ProductPrice from "@/components/pieces/products/ProductPrice";
import AllTimeslotToggle from "@/components/pieces/schedule/SelectTimeSession/AllTimeslotToggle";
import DateTimeSelector from "@/components/pieces/schedule/SelectTimeSession/DateTimeSlotSelector";
import { RequestBookingText } from "@/components/pieces/schedule/SelectTimeSession/shared";
import { TimeZoneSelectorContainerStyled } from "@/components/pieces/schedule/SelectTimeSession/styles";
import { ViewTimeSessionBoxStyled } from "@/components/pieces/schedule/ViewAvailableTimeWidget/style";
import { CARD_CLASSES, HEADING_FONT } from "@/css/constants";
import { AgentProfileQuery, CoachProductsQuery } from "@/graphql";

interface AssistantClientOption {
  value: string | number;
  label: string;
}

interface BookingCoachBoxProps {
  timeSlotData: string[];
  loading?: boolean;
  onChangeTimeZone: (option: { value: string }) => void;
  onChangeProduct: (product: CoachProductsQuery["coachProducts"][0]) => void;
  timeZone: string;
  specialties?: SpecialtyRenderDataType[];
  isCoachOwner?: boolean;
  submitting?: boolean;
  onSubmit?: () => void;
  error?: any;
  coach?: AgentProfileQuery["agentProfile"]["user"];
  onCancelError?: () => void;
  onConfirmError?: () => void;
  selectedCoachProduct?: CoachProductsQuery["coachProducts"][0];
  selectedBookingTimes?: any[];
  onSelectedBookingTimes?: (times: any[]) => void;
  availableTimeSlotData?: string[];
  assistantClientOptions?: AssistantClientOption[];
  selectedClientId?: string | number;
  onChangeClient?: (opt: AssistantClientOption) => void;
  onToggleUsedAllTimeslot?: () => void;
  availableBooking?: boolean;
  profile?: Partial<AgentProfileQuery["agentProfile"]>;
  availableChageProducts?: boolean;
  availableChangeSpecialties?: boolean;
  usedAllTimeslot?: boolean;
}

export default function BookingCoachBox({
  timeSlotData,
  loading = false,
  onChangeTimeZone,
  onChangeProduct,
  timeZone,
  specialties,
  isCoachOwner = false,
  submitting = false,
  onSubmit,
  error,
  coach,
  onCancelError,
  onConfirmError,
  selectedCoachProduct,
  selectedBookingTimes,
  onSelectedBookingTimes,
  availableTimeSlotData = [],
  assistantClientOptions = [],
  selectedClientId = null,
  onChangeClient,
  onToggleUsedAllTimeslot = () => {},
  availableBooking = true,
  profile,
  availableChageProducts = true,
  availableChangeSpecialties = true,
  usedAllTimeslot = false,
}: BookingCoachBoxProps) {
  const productSlug = selectedCoachProduct?.product?.slug;

  useEffect(() => {
    if (specialties && specialties.length > 0) {
      setSelectedSpecialtyId(specialties[0].id);
    }
  }, [specialties]);

  const [selectedSpecialtyId, setSelectedSpecialtyId] = useState<
    string | number | null
  >(null);

  const isRequestBooking =
    selectedCoachProduct?.product?.enableBookingRequest &&
    selectedCoachProduct?.requireBookingRequest;

  const bookingTitle = isRequestBooking
    ? "Request Booking"
    : "Schedule Session";
  const title = isCoachOwner ? "Preview Schedule" : bookingTitle;
  const isMobile = useMediaQuery(isMobileQuery);
  // Just get meeting product
  const selectedSpecialties = specialties.filter(
    (item) => item.id === selectedSpecialtyId,
  );
  const selectedSpecialty =
    selectedSpecialties && selectedSpecialties.length > 0
      ? selectedSpecialties[0]
      : null;
  const meetingCategory = selectedSpecialty
    ? selectedSpecialty.categories
    : null;
  const products =
    meetingCategory && meetingCategory.length > 0
      ? [].concat(...meetingCategory.map((item) => item.products))
      : [];

  // If user have not intro with coach, user just book intro meeting
  const introProducts = products.filter((item) => item.introProduct);

  // In BookingCoachBox
  const productsOptions = useMemo(() => {
    return (
      introProducts.length > 0 && !isCoachOwner ? introProducts : products
    ).map((item) => ({
      value: item.slug,
      label: item.name,
      description: `${item.duration} Minutes`,
    }));
  }, [introProducts, products, isCoachOwner]);

  const productBySlug = useMemo(() => {
    return products.reduce((acc, item) => {
      acc[item.slug] = item;
      return acc;
    }, {});
  }, [products, selectedSpecialtyId]);

  const onSelectProduct = (option) => {
    onChangeProduct(productBySlug[option.value]);
  };

  useEffect(() => {
    if (selectedSpecialtyId && productsOptions?.length > 0 && !productSlug) {
      onChangeProduct(productBySlug[productsOptions[0].value]);
    }
  }, [selectedSpecialtyId]); // Only depend on specialty changes

  const disableSubmit =
    submitting || selectedBookingTimes?.length === 0 || !productSlug;
  const isLongText =
    productsOptions.findIndex((item) => item.label.length > 20) > 0;
  const displayType =
    productsOptions.length === 1
      ? "text"
      : isMobile || isLongText
        ? "dropdown"
        : "list";

  if (!availableBooking) {
    return (
      <ViewTimeSessionBoxStyled
        className={CARD_CLASSES}
        backgroundColor="#FFF"
        px={[3, 3, 3, 24]}
        m={[3, 0]}
      >
        {isAssist() && (
          <>
            <Box
              mb={10}
              pt={[3, 3, 3, 24]}
              css={{ fontSize: 24, fontWeight: 600, fontFamily: HEADING_FONT }}
            >
              {title}
            </Box>
            <Box fontWeight={600}>Schedule Session For</Box>
            <AssistantClientsSelector
              value={selectedClientId}
              options={assistantClientOptions}
              onChange={(opt) => {
                onChangeClient?.(opt);
              }}
              className="select-time-range select-time-range-mini"
            />
            <Box className="bbs" mt={3} />
          </>
        )}
        <AlertMessage
          name={profile?.user?.name}
          message={`is not accepting new clients right now`}
        />
      </ViewTimeSessionBoxStyled>
    );
  }

  const timeSlotDataObjects = timeSlotData.map((timeSlot) => ({
    timeSlot,
    isPreferred: false,
  }));
  const availableTimeSlotDataObjects = availableTimeSlotData.map(
    (timeSlot) => ({
      timeSlot,
      isPreferred: false,
    }),
  );

  return (
    <ViewTimeSessionBoxStyled
      className={CARD_CLASSES}
      backgroundColor="#FFF"
      p={[3, 3, 3, 24]}
      m={[3, 0]}
    >
      <Box
        mb={10}
        css={{ fontSize: 24, fontWeight: 600, fontFamily: HEADING_FONT }}
      >
        {title}
      </Box>

      <Box fontWeight={600} mt={10}>
        <Flex className="timeslot-wrapper">
          <Box className="timeslot-content" css={{ width: "100%" }}>
            <Box className="timezone-wrapper" css={{ width: "100%" }}>
              {isAssist() && (
                <TimeZoneSelectorContainerStyled>
                  <Box>Schedule Session For</Box>
                  <AssistantClientsSelector
                    value={selectedClientId}
                    options={assistantClientOptions}
                    onChange={(opt) => {
                      onChangeClient(opt);
                    }}
                    className="select-time-range select-time-range-mini"
                  />
                  <Box className="bbs" my={3} />
                </TimeZoneSelectorContainerStyled>
              )}
              {!availableChageProducts ? (
                isRequestBooking && <RequestBookingText hasBorder />
              ) : (
                <TimeZoneSelectorContainerStyled>
                  {availableChangeSpecialties && (
                    <SpecialtiesSelector
                      value={selectedSpecialtyId}
                      options={specialties.map((option) => ({
                        value: option.id,
                        label: option.name,
                      }))}
                      onChange={(item) => {
                        setSelectedSpecialtyId(item.value);
                      }}
                      className="select-time-range select-time-range-mini"
                    />
                  )}
                  <ProductSelector
                    value={productSlug}
                    onChange={onSelectProduct}
                    className="select-time-range select-time-range-mini"
                    displayType={displayType}
                    defaultOptions={productsOptions}
                  />
                  {isRequestBooking && <RequestBookingText />}
                  <Box className="bbs" my={3} />
                </TimeZoneSelectorContainerStyled>
              )}
            </Box>
            <DateTimeSelector
              coach={coach}
              selectedSlots={selectedBookingTimes}
              onSelectedBookingTimes={onSelectedBookingTimes}
              onChangeTimeZone={onChangeTimeZone}
              loading={loading}
              timeSlotData={timeSlotDataObjects}
              availableTimeSlotData={availableTimeSlotDataObjects}
              timeZone={timeZone}
              isMultiselect={isRequestBooking}
              usedAllTimeslot={usedAllTimeslot}
              onToggleUsedAllTimeslot={onToggleUsedAllTimeslot}
            />
            <AllTimeslotToggle
              coach={coach}
              usedAllTimeslot={usedAllTimeslot}
              onToggleUsedAllTimeslot={onToggleUsedAllTimeslot}
            />
          </Box>
        </Flex>

        <Box className="bbs" my={3} />
        {selectedCoachProduct ? (
          <ProductPrice
            product={selectedCoachProduct?.product}
            creditPrice={selectedCoachProduct?.price}
            usdPrice={selectedCoachProduct?.usdPrice}
          />
        ) : null}

        <ErrorModalContainer
          error={error}
          visible={error}
          onCancel={() => {
            onCancelError();
          }}
          onConfirm={() => {
            onConfirmError();
          }}
        />
      </Box>
      {!isCoachOwner && (
        <Box mt={24}>
          <BigButton
            onClick={onSubmit}
            intent="primary"
            large={true}
            fill={true}
            loading={submitting}
            disabled={disableSubmit}
          >
            {isRequestBooking ? "Request Booking" : "Book Now"}
          </BigButton>
        </Box>
      )}

      <Flex
        className="loading-overlay"
        css={{
          display: loading ? "flex" : "none",
          zIndex: loading ? 10 : -1,
        }}
      >
        <Spinner />
      </Flex>
    </ViewTimeSessionBoxStyled>
  );
}
